import { createFileRoute } from '@tanstack/react-router';
import { EvaluationTypeIcon } from '@/app/components/features/evaluations/EvaluationTypeIcon';
import { getTenderPackageDetailsQuery } from '@/app/hooks/queries/useTenderPackages';
import {
  Breadcrumb,
  BreadcrumbMenu,
  BreadcrumbMenuItem,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@/app/ui/components/breadcrumb';
import {
  Header,
  HeaderSidechatButton,
  HeaderBackButton,
  HeaderSidebarTrigger,
} from '@/app/ui/components/header';
import { RunAnalysisHeaderButton } from '@/app/components/features/evaluations/RunAnalysisButton';
import {
  DocumentTypes,
  EvaluationTypeLabelsEnum,
  SummarizationNamespaces,
} from '@/app/utils/constants';
import { SearchFocusIcon, PlusSignIcon } from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import { useTranslations } from 'use-intl';
import { ExtractionsPageContent } from '@/app/components/features/evaluations/Extractions/ExtractionsPageContent';
import { useSetAtom } from 'jotai';
import { createExtractionOpenAtom } from '@/app/components/features/evaluations/Extractions/store';
import { ProjectTypeIcon } from '@/app/components/features/projects/ProjectTypeIcon';
import { getProjectDetailsQuery } from '@/app/hooks/queries/useProjects';
import { useSuspenseQuery } from '@tanstack/react-query';
import { validator } from '@/app/validator';
import z from 'zod';

export const Route = createFileRoute(
  '/_authed/project/$projectId/_layout/(evaluation)/tender-packages/$tenderPackageId/_layout/(main)/extractions/',
)({
  component: RouteComponent,
  validateSearch: validator(
    z.object({
      id: z.string().optional(),
      extractionId: z.string().optional(),
      queryId: z.string().optional(),
    }),
  ),
});

function RouteComponent() {
  const t = useTranslations('pages.tenderHome');
  const tFeature = useTranslations('features.extractions');
  const { tenderPackageId, projectId } = Route.useParams();
  const { data: evaluation } = useSuspenseQuery(getTenderPackageDetailsQuery(tenderPackageId));
  const { data: project } = useSuspenseQuery(getProjectDetailsQuery(projectId));
  const summaryType = SummarizationNamespaces.TENDER_PACKAGE;
  const setIsNewModalOpen = useSetAtom(createExtractionOpenAtom);

  return (
    <>
      <Header>
        <HeaderBackButton
          to="/project/$projectId/tender-packages/$tenderPackageId"
          params={{ projectId, tenderPackageId }}
        />
        <HeaderSidebarTrigger />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbMenu>
              <BreadcrumbMenuItem to="/project/$projectId" params={{ projectId }}>
                <ProjectTypeIcon type={project.type} />
                {project.name}
              </BreadcrumbMenuItem>
            </BreadcrumbMenu>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <EvaluationTypeIcon type={EvaluationTypeLabelsEnum.TENDER_PACKAGE} />
                <span className="line-clamp-1">{evaluation.name}</span>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem className="max-md:hidden">
              <BreadcrumbLink
                to="/project/$projectId/tender-packages/$tenderPackageId"
                params={{ projectId, tenderPackageId }}
              >
                <HugeiconsIcon icon={SearchFocusIcon} />
                {t('title')}
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator className="max-md:hidden" />
            <BreadcrumbItem>
              <BreadcrumbPage>{tFeature('title')}</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <HeaderSidechatButton />
        <RunAnalysisHeaderButton
          entityId={tenderPackageId}
          documentType={DocumentTypes.TENDER_PACKAGE}
          onClick={() => setIsNewModalOpen(true)}
          aria-label={tFeature('buttons.create')}
        >
          <HugeiconsIcon icon={PlusSignIcon} />
        </RunAnalysisHeaderButton>
      </Header>
      <ExtractionsPageContent
        summaryType={summaryType}
        evaluationId={tenderPackageId}
        evalLanguage={evaluation.language}
      />
    </>
  );
}
